import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { Observable, catchError, of, tap } from 'rxjs';
import { AuthResponse, User } from './models';

const TOKEN_KEY = 'liiji.token';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly userState = signal<User | null>(null);
  private readonly session = signal(typeof localStorage !== 'undefined' && !!localStorage.getItem(TOKEN_KEY));

  readonly user = this.userState.asReadonly();
  readonly signedIn = this.session.asReadonly();

  constructor() {
    if (localStorage.getItem(TOKEN_KEY)) {
      this.http.get<User>('/api/auth/me').subscribe({
        next: (user) => this.userState.set(user),
        error: () => this.clear(),
      });
    }
  }

  register(name: string, email: string, phone: string, country: string, password: string): Observable<AuthResponse> {
    return this.http
      .post<AuthResponse>('/api/auth/register', { name, email, phone, country, password })
      .pipe(tap((response) => this.persist(response)));
  }

  login(email: string, password: string): Observable<AuthResponse> {
    return this.http
      .post<AuthResponse>('/api/auth/login', { email, password })
      .pipe(tap((response) => this.persist(response)));
  }

  ensureUser(): Observable<User | null> {
    const current = this.userState();
    if (current) {
      return of(current);
    }
    if (!localStorage.getItem(TOKEN_KEY)) {
      return of(null);
    }
    return this.http.get<User>('/api/auth/me').pipe(
      tap((user) => this.userState.set(user)),
      catchError(() => {
        this.clear();
        return of(null);
      }),
    );
  }

  saveDetails(body: {
    address: string;
    city: string;
    postalCode: string;
    residence: string;
    nationality: string;
    dateOfBirth: string;
  }): Observable<User> {
    return this.http.post<User>('/api/auth/details', body).pipe(tap((user) => this.userState.set(user)));
  }

  refresh(): void {
    if (!localStorage.getItem(TOKEN_KEY)) {
      return;
    }
    this.http.get<User>('/api/auth/me').subscribe({
      next: (user) => this.userState.set(user),
      error: () => this.clear(),
    });
  }

  signOut(): void {
    this.clear();
  }

  remember(user: User): void {
    this.session.set(true);
    this.userState.set(user);
  }

  staff(): boolean {
    const role = this.userState()?.role;
    return role === 'ADMIN' || role === 'AGENT';
  }

  private persist(response: AuthResponse): void {
    localStorage.setItem(TOKEN_KEY, response.token);
    this.session.set(true);
    this.userState.set(response.user);
  }

  private clear(): void {
    localStorage.removeItem(TOKEN_KEY);
    this.session.set(false);
    this.userState.set(null);
  }
}
